const REPLY_COMPOSER_SELECTOR = '[data-testid="reply-composer"]'
const KEYBOARD_INPUT_SELECTOR =
    '[contenteditable="true"], textarea, [role="textbox"], input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], input[type="tel"], input[type="number"]'

export const NATIVE_KEYBOARD_CHANGE_EVENT = 'nativeKeyboardChange'

export function normalizeNativeKeyboardState(value) {
    if (!value || typeof value.visible !== 'boolean') return null

    const rawHeight = Number(value.height)
    const height = value.visible && Number.isFinite(rawHeight) ? Math.max(0, rawHeight) : 0
    return { visible: value.visible, height }
}

export function readNativeKeyboardState(targetWindow) {
    if (targetWindow?.KNOVIGATOR_NATIVE_CAPS?.keyboardState !== true) return null
    return normalizeNativeKeyboardState(targetWindow.KNOVIGATOR_NATIVE_KEYBOARD)
}

export function reconcileNativeKeyboardState({
    currentState,
    nextState,
    ignoreHeightChanges = false,
}) {
    const normalizedNextState = normalizeNativeKeyboardState(nextState)
    if (!normalizedNextState) return null

    const normalizedCurrentState = normalizeNativeKeyboardState(currentState)
    if (
        normalizedCurrentState &&
        normalizedCurrentState.visible === normalizedNextState.visible &&
        (ignoreHeightChanges || normalizedCurrentState.height === normalizedNextState.height)
    ) {
        return currentState
    }

    return normalizedNextState
}

export function shouldUseViewportKeyboardFallback({
    isExpoAndroid,
    isOldMobile,
    hasNativeKeyboardState,
}) {
    return Boolean(!hasNativeKeyboardState && (isExpoAndroid || isOldMobile))
}

export function shouldObserveViewportKeyboard({
    isExpoAndroid,
    isOldMobile,
    isMobile,
    hasVisualViewport,
    hasNativeKeyboardState,
}) {
    // The native shell's keyboard bridge is authoritative when present; sampling viewport
    // geometry alongside it commits state resolveKeyboardState ignores.
    return Boolean(
        !hasNativeKeyboardState && !isExpoAndroid && !isOldMobile && isMobile && hasVisualViewport,
    )
}

export function resolveViewportKeyboardHeight({
    currentHeight,
    nextHeight,
    keyboardOpen,
    ignoreHeightChanges = false,
}) {
    if (!keyboardOpen) return 0

    const rawNextHeight = Number(nextHeight)
    const normalizedNextHeight = Number.isFinite(rawNextHeight) ? Math.max(0, rawNextHeight) : 0
    if (ignoreHeightChanges && currentHeight > 0) return currentHeight
    return normalizedNextHeight
}

// How much room the panel must reserve at its foot for the iOS keyboard.
//
// iOS does not shrink the layout viewport when the keyboard opens —
// window.innerHeight does not move, and the panel scroll box is 100dvh, so its
// clientHeight does not either. What moves is the VISUAL viewport: it loses the
// keyboard's height, and, because this app's document cannot scroll (the panels
// scroll internally), WebKit reveals the caret by shifting the visual viewport
// down inside the layout viewport instead. That shift is visualViewport.offsetTop.
//
// The reserve is in-flow content at the bottom of the scroll box (Panel.jsx), so
// every pixel of it is a pixel of extra scroll range — and PanelState's
// scrollToBottom spends the whole range, holding it there to within 5px. Reserve
// the raw occlusion while the viewport is ALSO shifted down by offsetTop and the
// shift is counted twice: the panel scrolls its content up by offsetTop further
// than the keyboard actually covers, and the message just posted is carried off
// the top of what the user can see. Netting the shift out lands the composer's
// foot on the keyboard's top edge whether or not WebKit has shifted anything.
//
// Android never arrives here: interactive-widget=resizes-content shrinks its
// layout viewport for real, so Panel.jsx holds this reserve at zero by design.
//
// This is the house formula, not a new one: AiMagicWandMenuButton's keyboardInset
// is innerHeight - visualViewport.height - visualViewport.offsetTop, and
// PanelContainer reads the visible bottom as offsetTop + height. The panel's own
// keyboard reserve was the one place that dropped the offsetTop term.
//
// Detection is deliberately NOT computed from this: a large shift can pull the
// remaining occlusion under the open-threshold, and a keyboard that reports
// itself closed would collapse the reserve entirely.
export function resolveIosKeyboardReserve({ occlusion, pageShift = 0 }) {
    const base = Math.max(0, Number(occlusion) || 0)
    const shift = Math.max(0, Number(pageShift) || 0)
    return Math.max(0, base - shift)
}

export function shouldRevealNewAnswerOnKeyboardOpen({ keyboardOpen, isAndroid }) {
    return Boolean(keyboardOpen && isAndroid)
}

export function shouldRetainReplyComposerKeyboardFallback({
    isAndroidMobileApp,
    hasNativeKeyboardState,
    replyComposerFocused,
}) {
    // Legacy Android WebViews expose no dependable browser-side keyboard close event.
    // Prefer hiding the navigation until focus leaves; the native bridge supersedes this fallback.
    return Boolean(isAndroidMobileApp && !hasNativeKeyboardState && replyComposerFocused)
}

export function resolveKeyboardState({
    nativeKeyboardState,
    viewportKeyboardOpen,
    viewportKeyboardHeight,
}) {
    const nativeState = normalizeNativeKeyboardState(nativeKeyboardState)
    if (nativeState) return nativeState

    const rawHeight = Number(viewportKeyboardHeight)
    return {
        visible: Boolean(viewportKeyboardOpen),
        height: viewportKeyboardOpen && Number.isFinite(rawHeight) ? Math.max(0, rawHeight) : 0,
    }
}

export function resolvePanelKeyboardHeight({
    keyboardState,
    hasNativeKeyboardState,
    isIOS,
    visualViewportOffsetTop = 0,
}) {
    const normalizedState = normalizeNativeKeyboardState(keyboardState)
    const keyboardHeight = normalizedState?.height || 0

    // Browser-derived iOS height already uses resolveIosKeyboardReserve. The native
    // bridge supplies the raw keyboard height, so apply the same caret-shift correction
    // here instead of reserving visualViewport.offsetTop twice after a dismiss/reopen.
    if (!hasNativeKeyboardState || !isIOS) return keyboardHeight

    return resolveIosKeyboardReserve({
        occlusion: keyboardHeight,
        pageShift: visualViewportOffsetTop,
    })
}

// A field that cannot take typing never raises a virtual keyboard: react-select
// keeps focus on a read-only dummy input behind the composer's palette menu.
// Focusing it is not a keyboard on its way, so the navbar must not hide and
// come back for nothing.
const NO_KEYBOARD_INPUT_SELECTOR = '[inputmode="none"], input[readonly], textarea[readonly]'

export function isReplyComposerFocusTarget(target) {
    if (!target?.closest?.(REPLY_COMPOSER_SELECTOR)) return false
    const input = target.closest?.(KEYBOARD_INPUT_SELECTOR)
    if (!input) return false
    return !(input.matches?.(NO_KEYBOARD_INPUT_SELECTOR) ?? false)
}

// Whether the element holding focus is one the software keyboard serves.
export function isKeyboardInputElement(element) {
    if (!element?.matches) return false
    if (!element.matches(KEYBOARD_INPUT_SELECTOR)) return false
    return !element.matches(NO_KEYBOARD_INPUT_SELECTOR)
}

// Whether a focus change means the keyboard is on its way out. The keyboard's
// own state (the native bridge, the viewport) reports the dismissal only once
// the slide is over — on iOS a good 250ms plus a sample interval after "Done"
// — and everything that returns to the foot (the navbar, the composer pill)
// waited for it, arriving as a second beat after the keyboard had gone. Focus
// leaving every keyboard input is the dismissal's first sign: the keyboard
// goes when nothing it serves has focus. Focus moving to another keyboard
// input (the search bar, a poll option) keeps it, and the anticipation is
// dropped again if the keyboard's own state never follows.
//
// The page acts on the sign itself ('local'): the foot closes — navbar back,
// pill folded — and where the layout viewport is still shrunk for the keyboard
// the pill glides down ahead of the viewport growing back (bottomEditorGlide's
// pending shift), so the two arrive as one motion whenever the growth comes:
// with the keyboard's slide under an iOS shell, after it in Chrome. The
// Android shell is the exception ('shell'): it learns of the keyboard's
// departure from a layout pass — in its edge-to-edge window often a second
// after the keyboard has gone — so the sign is sent to it, and it regrows the
// WebView and publishes the keyboard hidden in the same tick.
export function resolveKeyboardDismissalAnticipation({
    keyboardOpen,
    activeElementIsKeyboardInput,
    shellAnswersDismissal = false,
}) {
    if (!keyboardOpen || activeElementIsKeyboardInput) return 'none'
    return shellAnswersDismissal ? 'shell' : 'local'
}

// Whether a keyboard can be expected at focus, ahead of its report, so the
// foot moves to meet it (shouldExpectKeyboardOnFocus). Everywhere but the
// Android shell without the opening capability: that shell reports its
// keyboard from a layout pass that can trail the keyboard by a second, and
// with nothing pre-shrinking the WebView the foot would float above nothing
// for that second and lapse. A browser resizes within the keyboard's own
// slide; the newer shell pre-shrinks on the opening message; iOS reports at
// the slide's start, and where its keyboard overlays the page the reserve
// arithmetic lands the foot exactly (the pill's programmatic focus keeps
// WebKit from panning the page on top).
export function canPredictKeyboardOpening({ isAndroidMobileApp, keyboardOpeningCapability }) {
    return !isAndroidMobileApp || Boolean(keyboardOpeningCapability)
}

// Where the layout viewport shrinks for the keyboard: Android, browser and
// app alike, and an iOS shell that resizes its WebView for it
// (KNOVIGATOR_NATIVE_CAPS.keyboardResize; treechat-mobile App.tsx). Elsewhere
// — iOS Safari, older iOS shells — the keyboard overlays the page and WebKit
// pans the visual viewport to reveal the caret instead.
export function viewportResizesForKeyboard({ isAndroid, nativeCaps }) {
    return Boolean(isAndroid || nativeCaps?.keyboardResize === true)
}

export function shouldAnticipateKeyboardDismissal(input) {
    return resolveKeyboardDismissalAnticipation(input) === 'local'
}

// The web → shell messages: JSON with a kind, like the back bridge's answers.
export const WEB_KEYBOARD_DISMISSING_KIND = 'keyboardDismissing'
export const WEB_KEYBOARD_OPENING_KIND = 'keyboardOpening'

export function buildKeyboardDismissingMessage() {
    return JSON.stringify({ kind: WEB_KEYBOARD_DISMISSING_KIND })
}

export function buildKeyboardOpeningMessage() {
    return JSON.stringify({ kind: WEB_KEYBOARD_OPENING_KIND })
}

// The arrival's first sign, the mirror of the departure's: focus landing in a
// keyboard input while no keyboard is up. The keyboard's own state confirms
// it a slide later (iOS), or a layout pass later (the Android shell) — and
// the panel foot used to wait for that: the navbar stood through the slide
// and went only once the keyboard was up, the composer dropping into its
// place a beat after. From the focus on, the foot takes the keyboard's
// layout at once, so the keyboard slides up to meet a composer already
// standing where its edge will be.
//
// Only once a keyboard has been seen, though — this load or an earlier one
// (keyboardHeightMemory): its height is where the pill glides to ahead of the
// viewport shrinking (bottomEditorGlide's pending shift), and what the Android
// shell replays to shrink the WebView early (buildKeyboardOpeningMessage).
// With no height there is no edge to head for, and the navbar going at focus
// would only drop the pill into its place to be lifted later; waiting for the
// keyboard's report lets the navbar go in the tick the viewport resizes.
//
// And only where the foot can act on it (canPredictKeyboardOpening).
export function shouldExpectKeyboardOnFocus({
    isMobile,
    keyboardOpen,
    targetIsKeyboardInput,
    lastKeyboardHeight,
    canPredictKeyboardOpening,
}) {
    return Boolean(
        isMobile &&
            !keyboardOpen &&
            targetIsKeyboardInput &&
            canPredictKeyboardOpening &&
            Number.isFinite(lastKeyboardHeight) &&
            lastKeyboardHeight > 0,
    )
}

// What the foot lays out for: the keyboard as reported, or, while one is
// expected and not yet reported, an open foot with no reserve. Where the
// viewport resizes for the keyboard (Android) the reserve is moot: the foot
// rides the viewport's edge. Where it does not (iOS), WebKit pans the visual
// viewport up by the keyboard's height to reveal the caret, so a pill at the
// layout's bottom rides that pan onto the keyboard's edge, and the reported
// reserve nets the pan out to about nothing (resolvePanelKeyboardHeight).
// Laying the foot out for the last keyboard's height at focus stacked the two:
// the pill went up by the height, was panned up by it again — off the top of
// the screen — and came back down when the keyboard reported.
export function resolveFootKeyboardState({ keyboardOpen, keyboardHeight, keyboardExpected }) {
    if (keyboardOpen) return { open: true, height: keyboardHeight }
    if (keyboardExpected) return { open: true, height: 0 }
    return { open: false, height: 0 }
}

// How long a focus-raised expectation stands with no keyboard reported, or
// null when the shell's own word ends it. A field can take focus and raise no
// keyboard (a hardware keyboard), so the expectation normally lapses after
// the fallback window. In the Android shell an expectation only ever stands
// alongside the shell's replay of its last keyboard (shouldExpectKeyboardOnFocus
// needs a seen keyboard), and the shell keeps that replay's own clock: if no
// keyboard follows it rolls the replay back with a hide report, which ends the
// expectation here (shouldEndKeyboardExpectationOnNativeReport). A second
// clock on the web side, lapsing early, only brought the navbar back under a
// keyboard the shell was still to report — its report can trail the keyboard
// by a second in an edge-to-edge window.
export function resolveKeyboardExpectationTimeoutMs({ isAndroidMobileApp, fallbackMs }) {
    return isAndroidMobileApp ? null : fallbackMs
}

// Whether a keyboard report from the shell ends a standing expectation. A
// visible report does through the keyboard's own state (the keyboard has
// arrived); a hidden one is the shell rolling back a replay no keyboard
// followed, or a keyboard leaving — either way nothing is coming.
export function shouldEndKeyboardExpectationOnNativeReport({ keyboardExpected, visible }) {
    return Boolean(keyboardExpected && !visible)
}

export const VIEWPORT_KEYBOARD_OPEN_THRESHOLD_PX = 100
// Once open, the keyboard is read as leaving when the occlusion has fallen to
// this fraction of the most it reached.
export const VIEWPORT_KEYBOARD_CLOSE_FRACTION = 0.6

// Whether the viewport says the keyboard is up, on a platform whose viewport
// shrinks for it and grows back as it leaves, in step with the slide. One
// threshold both ways read "open" a third of the way into the keyboard's
// arrival but "closed" only two thirds into its departure — the remaining
// occlusion had to fall under the open threshold — so the foot's return
// started late and ran on after the keyboard had gone. Closing now trips at
// the same point of the slide as opening: an occlusion down to 60% of its
// peak. The margin is what keeps a keyboard that merely changes height (an
// emoji panel giving way to letters, a suggestion strip going) from reading
// as gone, which would fold the composer and blur the field under the
// reader's thumbs.
export function resolveViewportKeyboardOpen({
    wasOpen,
    occlusion,
    peakOcclusion = 0,
    openThreshold = VIEWPORT_KEYBOARD_OPEN_THRESHOLD_PX,
    closeFraction = VIEWPORT_KEYBOARD_CLOSE_FRACTION,
}) {
    const current = Math.max(0, Number(occlusion) || 0)
    if (!wasOpen) return current > openThreshold
    const peak = Math.max(current, Number(peakOcclusion) || 0)
    return current >= Math.max(openThreshold, peak * closeFraction)
}

export function shouldHideBottomNavigation({
    isMobile,
    keyboardOpen,
    replyComposerKeyboardPending,
}) {
    // Focus is normally a bounded bridge until real keyboard state takes over. Legacy Android
    // app shells can retain this pending state until blur because they expose no close signal.
    return Boolean(isMobile && (keyboardOpen || replyComposerKeyboardPending))
}

export function shouldBlurNewAnswerAfterKeyboardClose({ isMobile, wasKeyboardOpen, keyboardOpen }) {
    return Boolean(isMobile && wasKeyboardOpen && !keyboardOpen)
}

export function shouldBlurNewAnswerBeforeExternalInteraction({
    isMobile,
    interactionInsideComposer,
}) {
    return Boolean(isMobile && !interactionInsideComposer)
}
